import React, { useState, useEffect } from 'react';
import { Save, Globe, Bell, Settings as SettingsIcon, User, Key, Mail } from 'lucide-react';
import { settingsService, SimpleSettings } from '../services/settingsService';
import { useAuth } from '../contexts/AuthContext';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../config/firebase';
import toast from 'react-hot-toast';

const Settings = () => {
  const [settings, setSettings] = useState<SimpleSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getSettings();
      setSettings(data);
    } catch (error: any) {
      toast.error('Failed to load settings');
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };


  const handlePasswordReset = async () => {
    if (!user?.email) {
      toast.error('No email address found');
      return;
    }

    try {
      setResettingPassword(true);
      await sendPasswordResetEmail(auth, user.email);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      toast.error('Failed to send password reset email');
    } finally {
      setResettingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <span className="ml-3 text-gray-600">Loading settings...</span>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="space-y-6">
        <div className="card">
          <div className="card-body text-center py-12">
            <div className="text-red-500 mb-4">
              <SettingsIcon className="w-12 h-12 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Settings</h3>
            <p className="text-gray-600">Failed to load settings. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-heading text-primary-800">Settings</h1>
          <p className="text-gray-600 mt-1">
            Basic CMS configuration
          </p>
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {settings.lastUpdated.toLocaleDateString()}
        </div>
      </div>

      {/* User Account Section */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">User Account</h3>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Logged in as</h4>
                  <p className="text-sm text-gray-600">{user?.email || 'No email found'}</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Key className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Password</h4>
                  <p className="text-sm text-gray-600">Reset your password</p>
                </div>
              </div>
              <button
                onClick={handlePasswordReset}
                disabled={resettingPassword}
                className="btn btn-secondary flex items-center gap-2"
              >
                {resettingPassword ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Key className="w-4 h-4" />
                )}
                {resettingPassword ? 'Sending...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      </div>


      {/* Info Card */}
      {/* <div className="card">
        <div className="card-body">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <SettingsIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">How it works</h4>
              <div className="text-sm text-gray-600 space-y-2">
                <p>• <strong>Enable Notifications:</strong> When turned on, users will receive notifications when you publish new quotes, e-books, or audio content.</p>
                <p>• <strong>Default Publish Status:</strong> Controls whether new content is published immediately or saved as draft by default.</p>
                <p>• <strong>Notification Email:</strong> The email address that will receive system notifications and alerts.</p>
              </div>
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default Settings;
